import React, { Fragment, useState } from 'react';
import { Container, Dropdown, Icon, Menu, Responsive } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { Link, NavLink } from 'react-router-dom';
import { compose } from 'react-apollo';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import Svg from 'react-inlinesvg';

import { withAuth, AuthStates, AuthRoles, authProptypes } from '../../auth';
import { Routes } from '../../routing';
import { Validations } from '../../utils';
import { Logo } from '../';
import { withAnalytics } from '../../analytics';
import { Menu as TopMenu } from '../';
import { breakpointsNumber } from '../../styles';
import MobileDropdown from './MobileDropdown';
import { StyledMobileNav, StyledFullscreenNav } from './TopNavigation.style';
import useWindowWidth from '../../hooks/useWindowWidth';
import { largeMonitorBreakpoint } from '../../utils/breakpoints';

import { hamburgerIcon } from '../../images';

const CloseIcon = styled(Icon)`
  padding: 0 !important;
`;

export const TopNavigation = props => {
  const [showDesktopNav, setShowDesktopNav] = useState(true);
  const [visible, setVisible] = useState(false);
  const [tabletMenuVisible, setTabletMenuVisible] = useState(false);
  const { windowWidth } = useWindowWidth();

  const {
    authState,
    authActions: { signOut },
    authUser,
    location = {},
    analytics
  } = props;

  const isAuthorized = authUser && authUser.isAuthorized;
  const isRep = Validations.hasRole(authUser, 'roles', [AuthRoles.Sales]);

  const showRep = isAuthorized && !isRep;

  const { givenName, lastName, name } = authUser;
  const username = givenName && lastName ? `${givenName} ${lastName}` : name;
  const isSignedIn = authState === AuthStates.SignedIn;
  const isDesktop = windowWidth > largeMonitorBreakpoint - 1;

  const handleHamburgerMenuClicked = () => setVisible(!visible);
  const handleSidebarHide = () => setVisible(false);

  const handleSalesGuidanceClicked = () => {
    handleSidebarHide();
    analytics.trackEvent('Tool Selected', {
      'Link Location': 'Navigation Bar',
      Tool: 'Selling Tool'
    });
  };

  const handleProductComparisonClicked = () => {
    handleSidebarHide();

    analytics.trackEvent('Tool Selected', {
      'Link Location': 'Navigation Bar',
      Tool: 'Comparison Tool'
    });
  };

  const handlePricingHelperClicked = () => {
    handleSidebarHide();

    analytics.trackEvent('Tool Selected', {
      'Link Location': 'Navigation Bar',
      Tool: 'Pricing Helper'
    });
  };

  const logOut = () => {
    analytics.trackEvent('Logout', {
      Timestamp: moment().format('YYYY/MM/DD*HH:mm:ss')
    });
    signOut();
  };

  const links = {
    sales_tool: (
      <Menu.Item
        as={NavLink}
        exact
        to={Routes.salestool}
        onClick={handleSalesGuidanceClicked}
        activeClassName="item--active"
      >
        <span className="item__inner" data-qa="button-nav-sales-guidance">
          <FormattedMessage
            id="cta.sales_tool"
            description="Nav Guided Sales Tool page link text"
            defaultMessage="Sales Guidance"
          />
        </span>
      </Menu.Item>
    ),

    comparison_tool: (
      <Menu.Item
        as={NavLink}
        exact
        to={Routes.compare}
        onClick={handleProductComparisonClicked}
        activeClassName="item--active"
      >
        <span className="item__inner" data-qa="button-nav-product-education">
          <FormattedMessage
            id="cta.comparison_tool"
            description="Nav Comparison Tool page link text"
            defaultMessage="Product Comparison"
          />
        </span>
      </Menu.Item>
    ),

    agi_rep: (
      <Menu.Item
        as={NavLink}
        exact
        to={Routes.rep}
        onClick={handleSidebarHide}
        activeClassName="item--active"
      >
        <span className="item__inner">
          <FormattedMessage
            id="cta.agi_rep"
            description="Nav My AGI Rep page link text"
            defaultMessage="My AGI Rep"
          />
        </span>
      </Menu.Item>
    ),
    pricing_helper: (
      <Menu.Item
        as={NavLink}
        exact
        to={Routes.pricing}
        onClick={handlePricingHelperClicked}
        activeClassName="item--active"
      >
        <span className="item__inner">
          <FormattedMessage
            id="cta.pricing"
            description="Nav Pricing Helper page link text"
            defaultMessage="Pricing Helper"
          />
        </span>
      </Menu.Item>
    ),

    login: (
      <Menu.Item
        as={NavLink}
        to={Routes.login}
        name="login"
        onClick={handleSidebarHide}
      >
        <span className="item__inner">
          <FormattedMessage
            id="cta.login"
            description="Nav login link text"
            defaultMessage="Login"
          />
        </span>
      </Menu.Item>
    ),

    logout: (
      <Menu.Item as={Link} to={Routes.home} name="logout" onClick={logOut}>
        <span className="item__inner">
          <FormattedMessage
            id="cta.logout"
            description="Nav Logout link text"
            defaultMessage="Logout"
          />
        </span>
      </Menu.Item>
    ),

    helpcentre: (
      <Menu.Item
        as={'a'}
        href="https://agidealersupport.zendesk.com/hc/en-us"
        className="link--fade"
        target="agi"
      >
        <div style={{'background-color': '#006a35', 'margin-left': '25px', 'color': '#ffffff', 'padding': '15px'}}>Additional<br />Resources</div>
      </Menu.Item>
    )
  };

  return (
    <Fragment>
      <div className={visible ? 'menu--visible' : ''}>
        {/* green header */}
        <Menu as="header" fixed="top" secondary>
          <Container fluid>
            <Menu.Item header>
              {isSignedIn && (
                <Responsive
                  as={Menu.Item}
                  data-qa="toggle-menu"
                  minWidth={breakpointsNumber.tablet}
                  onClick={() => {
                    console.log({ isDesktop });
                    return isDesktop
                      ? setShowDesktopNav(!showDesktopNav)
                      : setTabletMenuVisible(!tabletMenuVisible);
                  }}
                  position="left"
                >
                  <Svg name="sidebar" src={hamburgerIcon} />
                </Responsive>
              )}
              <Logo />
            </Menu.Item>

            <Menu.Menu position="right">
              {isSignedIn && (
                <Responsive
                  as={Fragment}
                  maxWidth={breakpointsNumber.tablet - 1}
                >
                  {links.helpcentre}
                  <Menu.Item
                    data-qa="toggle-menu"
                    onClick={handleHamburgerMenuClicked}
                  >
                    {visible ? (
                      <CloseIcon name="close" />
                    ) : (
                      <Svg name="sidebar" src={hamburgerIcon} />
                    )}
                  </Menu.Item>
                </Responsive>
              )}

              <Responsive as={Fragment} minWidth={breakpointsNumber.tablet}>
                {isSignedIn ? (
                  <Fragment>
                    <Dropdown
                      trigger={
                        <Menu.Item>
                          <span
                            className="link--boxed"
                            data-qa="button-top-navigation-logout"
                          >
                            {username}
                          </span>
                          <Icon name="angle down" color="green" />
                        </Menu.Item>
                      }
                      pointing
                      icon={null}
                    >
                      <Dropdown.Menu>
                        {showRep && (
                          <Fragment>
                            {links.agi_rep}
                            <Dropdown.Divider />
                          </Fragment>
                        )}

                        {links.logout}
                      </Dropdown.Menu>
                    </Dropdown>
                    {links.helpcentre}
                  </Fragment>
                ) : (
                  // hide login link on login page
                  location.pathname !== Routes.login && links.login
                )}
              </Responsive>
            </Menu.Menu>
          </Container>
        </Menu>

        {/* top menu only for mobile */}
        {visible && (
          <StyledMobileNav>
            <TopMenu
              mobile
              location={location}
              setMobileMenuVisible={vis => setVisible(vis)}
            />
          </StyledMobileNav>
        )}
        {/* profile dropdown for mobile */}
        {isSignedIn && (
          <Responsive as={Fragment} maxWidth={breakpointsNumber.tablet - 1}>
            <MobileDropdown
              username={username}
              logOut={logOut}
              showRep={showRep}
            />
          </Responsive>
        )}
      </div>
      {showDesktopNav && (
        <StyledFullscreenNav tabletMenuVisible={tabletMenuVisible}>
          <TopMenu
            location={location}
            tabletMenuVisible={tabletMenuVisible}
            closeTabletMenu={() => setTabletMenuVisible(false)}
          />
        </StyledFullscreenNav>
      )}
    </Fragment>
  );
};

TopNavigation.propTypes = {
  // from withAuth
  authState: authProptypes.state,
  authActions: authProptypes.actions,
  authUser: authProptypes.user,
  // from withRouter
  location: PropTypes.shape({
    pathname: PropTypes.string
  }),
  analytics: PropTypes.object,
  toggleMenu: PropTypes.func,
  menu: PropTypes.bool
};

export default compose(
  withAuth,
  withRouter,
  withAnalytics
)(TopNavigation);



// WEBPACK FOOTER //
// ./src/components/TopNavigation/TopNavigation.js